<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import emptyBox from '@/assets/img/emptyBox.json';
import ListGroup from '@/components/ui/ListGroup.vue';
import LottieAnimation from '@/components/ui/LottieAnimation.vue';
import SearchResultItem from './SearchResultItem.vue';
import { closeSearch } from './close-search';
import type { Route, Stop } from '@/@types/gtfs-ressource';
import { isKnownRecentSearch, type RecentSearch } from '@/libs/helpers/recent-searches';
import { buildRouteSearchIndex, buildStopSearchIndex, searchIndex } from '@/libs/helpers/search';
import { useAppStore } from '@/stores/app';
import { useGtfsStore } from '@/stores/gtfs';
import { useRecentSearchesStore } from '@/stores/recent-searches';
import { useSearchStore } from '@/stores/search';

const SEARCH_DEBOUNCE_MS = 150;

const appStore = useAppStore();
const gtfsStore = useGtfsStore();
const recentSearchesStore = useRecentSearchesStore();
const searchStore = useSearchStore();
const router = useRouter();

// #region query
const debouncedQuery = ref<string>('');
let debounceTimeout: number | undefined;

watch(
  () => searchStore.query,
  value => {
    window.clearTimeout(debounceTimeout);
    // Clearing the field brings the recent searches back at once.
    if (!value) {
      debouncedQuery.value = '';
      return;
    }
    debounceTimeout = window.setTimeout(() => {
      debouncedQuery.value = value;
    }, SEARCH_DEBOUNCE_MS);
  },
  { immediate: true },
);

onBeforeUnmount(() => window.clearTimeout(debounceTimeout));
// #endregion

// #region results
const stopIndex = computed(() =>
  buildStopSearchIndex(Object.values(gtfsStore.gtfsData.stops), gtfsStore.canonicalOf),
);
const routeIndex = computed(() => buildRouteSearchIndex(Object.values(gtfsStore.gtfsData.routes)));

const stopResults = computed<Stop[]>(() => searchIndex(stopIndex.value, debouncedQuery.value));
const routeResults = computed<Route[]>(() => searchIndex(routeIndex.value, debouncedQuery.value));
const hasResults = computed<boolean>(() => stopResults.value.length > 0 || routeResults.value.length > 0);
// #endregion

// #region recent searches
interface ResolvedRecentSearch {
  entry: RecentSearch;
  route?: Route;
  stop?: Stop;
}

const isGtfsLoaded = computed<boolean>(() => Object.keys(gtfsStore.gtfsData.stops).length > 0);

const recentSearches = computed<ResolvedRecentSearch[]>(() =>
  recentSearchesStore.items
    .map(entry => ({
      entry,
      route: entry.type === 'route' ? gtfsStore.gtfsData.routes[entry.id] : undefined,
      stop: entry.type === 'stop' ? gtfsStore.gtfsData.stops[entry.id] : undefined,
    }))
    .filter(recent => recent.route || recent.stop),
);

// Only once the GTFS is there: pruning against empty data would wipe the history.
watch(
  isGtfsLoaded,
  loaded => {
    if (!loaded) return;
    recentSearchesStore.prune(entry => isKnownRecentSearch(entry, gtfsStore.gtfsData));
  },
  { immediate: true },
);
// #endregion

function remember(entry: RecentSearch) {
  recentSearchesStore.add(entry);
}

// Android back closes the search before the home sheet underneath gets a say.
let unregisterBackHandler: (() => void) | undefined;

onMounted(() => {
  unregisterBackHandler = appStore.registerBackHandler(() => {
    closeSearch(router);
    return true;
  });
});

onBeforeUnmount(() => unregisterBackHandler?.());
</script>

<template>
  <div class="search-panel">
    <div class="search-panel__results">
      <template v-if="debouncedQuery">
        <ListGroup v-if="stopResults.length" :label="$t('search.sectionStops')">
          <SearchResultItem v-for="stop in stopResults" :key="stop.stop_id" :stop="stop" @open="remember" />
        </ListGroup>

        <ListGroup v-if="routeResults.length" :label="$t('search.sectionRoutes')">
          <SearchResultItem
            v-for="route in routeResults"
            :key="route.route_id"
            :route="route"
            @open="remember"
          />
        </ListGroup>

        <div v-if="!hasResults" class="search-panel__empty">
          {{ $t('search.noResults') }}
          <LottieAnimation :animation-data="emptyBox" class="search-panel__empty-animation" />
        </div>
      </template>

      <ListGroup v-else-if="recentSearches.length" :label="$t('search.recent')">
        <SearchResultItem
          v-for="{ entry, route, stop } in recentSearches"
          :key="`${entry.type}-${entry.id}`"
          :route="route"
          :stop="stop"
          @open="remember"
        />
      </ListGroup>
    </div>
  </div>
</template>

<style lang="scss">
.search-panel {
  background-color: $color-white;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  inset: 0;
  // Leaves room for the search bar hosted by MapIndex, plus its 12px bottom
  // padding in the active state, under the status-bar band.
  padding-top: calc(var(--search-top-bar-height) + 12px + var(--status-bar-band));
  position: fixed;
  z-index: 2;

  &__results {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: $spacing-3;
    overflow-y: auto;
    // The WebView keeps its size under the keyboard: pad by its height so the
    // last results can still be scrolled into view (see capacitor.ts).
    padding: $spacing-4 0 calc(#{$spacing-4} + env(safe-area-inset-bottom) + var(--keyboard-height, 0px));
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacing-4;
    color: $color-neutral-higher;
    font: $text-small;
    letter-spacing: $text-small-spacing;
    padding: $spacing-2 $spacing-5;
    text-align: center;
  }

  &__empty-animation {
    width: min(160px, 50vw);
    aspect-ratio: 1;
  }

  // Section labels read as words here ("Arrêts", "Lignes"), not as list letters.
  .list-group__header {
    text-transform: none;
  }
}
</style>
